import { useCallback, useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { messagesAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnPrimary, EmptyState, inputCls, panelCls } from '../../components/ui';
import { nyTime } from '../../lib/nyTime';

export default function ThreadsPage() {
  const [threads, setThreads] = useState(null);
  const [open, setOpen] = useState(null); // {thread, messages}
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    messagesAPI.adminThreads().then(({ data }) => setThreads(data.items))
      .catch(() => toast.error(t('Could not load threads')));
  }, []);
  useEffect(load, [load]);

  const openThread = (tid) => {
    messagesAPI.adminOpen(tid).then(({ data }) => { setOpen(data); load(); })
      .catch(() => toast.error(t('Could not load messages')));
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !open) return;
    setBusy(true);
    try {
      await messagesAPI.adminSend(open.thread.id, text.trim());
      setText('');
      openThread(open.thread.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Send failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold">{t('Messages')}</h1>
      <div className="grid gap-4 lg:grid-cols-[320px,1fr]">
        <div className={`${panelCls} max-h-[70vh] overflow-y-auto`}>
          {threads === null ? (
            <p className="p-4 text-sm text-mms-muted">{t('Loading…')}</p>
          ) : threads.length === 0 ? (
            <p className="p-4 text-sm text-mms-muted">{t('No conversations yet.')}</p>
          ) : (
            threads.map((th) => (
              <button key={th.id} onClick={() => openThread(th.id)}
                className={`block w-full border-b border-mms-border/60 px-4 py-3 text-left text-sm last:border-0 hover:bg-mms-raised/60 ${
                  open?.thread?.id === th.id ? 'bg-mms-raised' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{th.member?.name || `#${th.membership_number}`}</span>
                  {th.staff_unread > 0 && (
                    <span className="rounded-full bg-mms-accent px-1.5 text-[10px] font-bold text-mms-accent-ink mms-num">
                      {th.staff_unread}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-mms-faint">{th.last_snippet}</div>
              </button>
            ))
          )}
        </div>
        <div className={`${panelCls} flex h-[70vh] flex-col`}>
          {!open ? (
            <div className="flex flex-1 items-center justify-center">
              <EmptyState>{t('Select a conversation.')}</EmptyState>
            </div>
          ) : (
            <>
              <div className="border-b border-mms-border px-4 py-2.5 text-sm font-medium">
                {threads?.find((x) => x.id === open.thread.id)?.member?.name || t('Conversation')}
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {open.messages.map((m) => (
                  <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    m.from === 'staff' ? 'ml-auto bg-mms-accent text-mms-accent-ink' : 'bg-mms-raised'}`}>
                    {m.body}
                    <div className={`mt-1 text-[10px] ${m.from === 'staff' ? 'text-mms-accent-ink/70' : 'text-mms-faint'}`}>
                      {m.author} · {nyTime(m.at)}
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="flex gap-2 border-t border-mms-border p-3">
                <input className={inputCls} value={text} placeholder={t('Write a message…')}
                  onChange={(e) => setText(e.target.value)} />
                <button type="submit" disabled={busy || !text.trim()} className={btnPrimary}>
                  <Send size={14} /> {t('Send')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
